import { useState, useRef, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { useCreateDialog, useEditRoute } from '@/contexts/CreateDialogContext'
import posthog from 'posthog-js'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from '@tanstack/react-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Check, Copy, IdCard, LayoutList, Image, Loader2, Plus, Trash2, Wallet, CalendarIcon, Pencil, X, Building2, Users, User, Hash, Headset, Landmark, Target } from 'lucide-react'
import { format } from 'date-fns'
import type { DateRange } from 'react-day-picker'

import { CreateObjectButton } from '@/components/CreateObjectButton'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { SearchSelect } from '@/components/SearchSelect'
import { UserSelect } from '@/components/UserSelect'
import { Switch } from '@/components/ui/switch'
import { Card, CardHeader, CardIcon, CardTitle, CardContent, CardFooter } from '@/components/ui/card'
import { ListCardHeader } from '@/components/ListCardHeader'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Calendar } from '@/components/ui/calendar'
import {
  StatsBar,
  StatsBarId,
  StatsBarActivity,
} from '../../components/StatsBar'
import { InlineEditTitle } from '../../components/InlineEditTitle'
import { AuthService } from '@/services/core/auth'
import { OrdersApi, OrderInput, Order, OrderBudget, CurrencyAmount } from '@/services/orders/ordersApi'
import { TeamsApi } from '@/services/users/teamsApi'
import { AdvertisersApi } from '@/services/advertisers/advertisersApi'
import { UsersApi } from '@/services/users/usersApi'
import {
  LineItemsApi,
  LineItem,
  BIDDING_GOALS,
  biddingGoalOf,
  biddingEventTargetOf,
  customBiddingGoal,
  customEventIdOfGoal,
  type BiddingGoalValue,
} from '@/services/lineItems/lineItemsApi'
import { useLineItemFieldSettings } from '../../hooks/useLineItemFieldSettings'
import { useBiddingCustomEvents } from '../../hooks/useBiddingCustomEvents'
import { FieldLabel, FieldTooltip } from '../../components/FieldLabel'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { CreativesApi, Creative } from '@/services/creatives/creativesApi'
import { apiErrorDescription } from '@/services/core/apiError'
import { LineItemTable } from '../../components/LineItemTable'
import { CreativeTable } from '../../components/CreativeTable'
import { BulkActionBar } from '../../components/BulkActionBar'
import { AppShell } from '../../components/AppShell'
import { FormSkeleton } from '../../components/FormSkeleton'
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges'
import { useEditMode } from '../../hooks/useEditMode'
import { DetailList, DetailRow } from '../../components/DetailList'
import { UnsavedChangesDialog } from '../../components/UnsavedChangesDialog'
import { NetworkObjectLookup } from '../../components/NetworkObjectLookup'
import { StatusFilterButton } from '../../components/StatusFilter'
import { ListTabs, type ListTab } from '../../components/ListTabs'
import { ListCard, BulkActionsSlot, ColumnsSlot, TableActionsSlot } from '../../components/ListCard'
import { SearchInput } from '../../components/SearchInput'
import { filterBySearch } from '../../utils/search'
import { LineItemFilters, useLineItemFilters } from '../../components/LineItemFilters'
import { StatsPanel } from '../../components/StatsPanel'
import { DeliveryProgressCard } from '../../components/DeliveryProgressCard'
import { spanFlight } from '@/services/stats/statsRange'
import { OrderCopyDialog } from '../../components/OrderCopyDialog'
import { useStatsRange } from '../../hooks/useStatsRange'
import { useResolvedRange } from '../../hooks/useResolvedRange'
import { useLineItemStats } from '../../hooks/useLineItemStats'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { DeliveryStatusBadge } from '../../components/DeliveryStatusBadge'
import { ObjectTypeLabel } from '../../components/ObjectTypeLabel'
import { PoliticalBadge } from '@/components/political/PoliticalBadge'
import { PoliticalLinkStatus } from '@/components/political/PoliticalLinkStatus'
import { useNetwork } from '../../contexts/NetworkContext'
import { useCurrentUser } from '../../contexts/UserContext'
import { usePersistedState } from '@/hooks/usePersistedState'
import { useObjectCrumbs } from '../../hooks/useObjectCrumbs'
import { formatNumber, formatDate, parseCalendarDay } from '../../utils/format'
import { ObjectStateToggle, useSaveObjectState, type ObjectState } from '../../components/ObjectStateToggle'
import { ValidationWarnings } from '../../components/ValidationWarnings'
import { VersionHistoryButton } from '@/components/VersionHistoryButton'
import { CappingCard } from '@/components/capping/CappingCard'
import { hasRateLimitProblem, type RateLimit } from '@/services/earnings/rateLimits'
import type { ObjectiveType } from '@/services/networks/networkFieldSettings'

const posthogConfigured = Boolean(
  import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN && import.meta.env.VITE_PUBLIC_POSTHOG_HOST
)

/** What an order may be capped on — as in the old UI, never budget. */
const ORDER_CAPPING_TYPES: ObjectiveType[] = [
  'IMPRESSION',
  'RENDERED_IMPRESSION',
  'VISIBLE_IMPRESSION',
  'VIEWABLE_IMPRESSION',
  'CLICK',
]

function formatCurrency(ca?: CurrencyAmount): string {
  if (!ca || ca.amount == null) return '–'
  return `${ca.currency} ${formatNumber(ca.amount, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

// ── Budget progress ─────────────────────────────────────────────────────────

interface BudgetProgress {
  startDate: string
  endDate: string
  deliveryPct: number | null
  elapsedTimePct: number
  target: CurrencyAmount
  spend?: CurrencyAmount
}

/**
 * The budget period running today, as a flight the delivery card can read:
 * spend against the period's amount — after rollover, when the order rolls
 * over — and how much of the period has passed. Budget days are inclusive.
 */
function activeBudgetProgress(budget: OrderBudget[]): BudgetProgress | null {
  const now = Date.now()
  for (const b of budget) {
    const start = parseCalendarDay(b.startDay)
    const endDay = parseCalendarDay(b.endDay)
    const target = b.adjustedAmount ?? b.amount
    if (!start || !endDay || !target?.amount) continue
    const end = new Date(endDay.getFullYear(), endDay.getMonth(), endDay.getDate() + 1)
    if (now < start.getTime() || now >= end.getTime()) continue
    return {
      startDate: start.toISOString(),
      // The last moment of the end day, so the card shows the day itself.
      endDate: new Date(end.getTime() - 1).toISOString(),
      deliveryPct: b.spend ? (b.spend.amount / target.amount) * 100 : null,
      elapsedTimePct: ((now - start.getTime()) / (end.getTime() - start.getTime())) * 100,
      target,
      spend: b.spend,
    }
  }
  return null
}

// ── Budget card ─────────────────────────────────────────────────────────────

interface BudgetPeriodRow {
  _key: string
  startDate: string
  endDate: string
  amount: string
  currency: string
  adjustedAmount?: CurrencyAmount
  spend?: CurrencyAmount
}

function toBudgetRow(b: OrderBudget, idx: number): BudgetPeriodRow {
  return {
    _key: String(idx),
    startDate: b.startDay ?? '',
    endDate: b.endDay ?? '',
    amount: b.amount?.amount != null ? String(b.amount.amount) : '',
    currency: b.amount?.currency ?? 'NOK',
    adjustedAmount: b.adjustedAmount,
    spend: b.spend,
  }
}

interface BudgetCardProps {
  periods: BudgetPeriodRow[]
  rollover: boolean
  currency: string
  onPeriodsChange: (rows: BudgetPeriodRow[]) => void
  onRolloverChange: (v: boolean) => void
  smoothed: boolean
  onSmoothedChange: (v: boolean) => void
}

function BudgetCard({
  periods,
  rollover,
  currency,
  onPeriodsChange,
  onRolloverChange,
  smoothed,
  onSmoothedChange,
}: BudgetCardProps) {
  const { t } = useTranslation()
  const nextKey = useRef(periods.length)

  function addRow() {
    const key = String(nextKey.current++)
    onPeriodsChange([...periods, { _key: key, startDate: '', endDate: '', amount: '', currency }])
  }

  function removeRow(key: string) {
    onPeriodsChange(periods.filter((r) => r._key !== key))
  }

  function updateRow(key: string, patch: Partial<BudgetPeriodRow>) {
    onPeriodsChange(periods.map((r) => (r._key === key ? { ...r, ...patch } : r)))
  }

  return (
    <Card tone="money">
      <CardHeader>
        <CardTitle>
          <CardIcon icon={Wallet} />
          {t('orders.budget')}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {periods.length === 0 ? (
          <p className="px-4 py-3 text-sm text-muted-foreground">{t('orders.noBudget')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-(--ad-border) bg-(--ad-bg) text-(--ad-text-muted)">
                  <th className="px-3 py-2 text-left font-medium">{t('orders.startDate')} – {t('orders.endDate')}</th>
                  <th className="px-3 py-2 text-left font-medium">{t('orders.budget')}</th>
                  <th className="px-3 py-2 text-right font-medium">{t('orders.adjAmount')}</th>
                  <th className="px-3 py-2 text-right font-medium">{t('orders.spend')}</th>
                  <th className="px-2 py-2" />
                </tr>
              </thead>
              <tbody>
                {periods.map((row) => (
                  <tr key={row._key} className="border-b border-(--ad-border) last:border-0">
                    <td className="px-3 py-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button type="button" variant="outline" className="w-56 justify-start text-left font-normal">
                            <CalendarIcon className="mr-2 size-4 opacity-50" />
                            {row.startDate && row.endDate ? (
                              `${format(new Date(row.startDate), 'dd.MM.yyyy')} – ${format(new Date(row.endDate), 'dd.MM.yyyy')}`
                            ) : row.startDate ? (
                              format(new Date(row.startDate), 'dd.MM.yyyy')
                            ) : (
                              <span className="text-muted-foreground">{t('orders.startDate')} – {t('orders.endDate')}</span>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="range"
                            selected={
                              row.startDate && row.endDate
                                ? { from: new Date(row.startDate), to: new Date(row.endDate) }
                                : row.startDate
                                ? { from: new Date(row.startDate), to: undefined }
                                : undefined
                            }
                            onSelect={(range: DateRange | undefined) => {
                              updateRow(row._key, {
                                startDate: range?.from ? format(range.from, 'yyyy-MM-dd') : '',
                                endDate: range?.to ? format(range.to, 'yyyy-MM-dd') : '',
                              })
                            }}
                            numberOfMonths={2}
                          />
                        </PopoverContent>
                      </Popover>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5">
                        <Input
                          type="number"
                          min={0}
                          step="any"
                          value={row.amount}
                          onChange={(e) => updateRow(row._key, { amount: e.target.value })}
                          placeholder={t('orders.budgetRequired')}
                          className="w-32"
                        />
                        <span className="px-2 py-1 rounded text-xs font-medium bg-(--ad-bg) border border-(--ad-border) text-(--ad-text-muted) select-none">
                          {currency}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {formatCurrency(row.adjustedAmount)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {formatCurrency(row.spend)}
                    </td>
                    <td className="px-2 py-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive"
                        onClick={() => removeRow(row._key)}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {periods.length > 0 && (
          <p className="px-4 py-2 text-xs text-(--ad-text-muted) border-t border-(--ad-border)">{t('orders.adjAmountHelp')}</p>
        )}
      </CardContent>
      <CardFooter className="flex items-center justify-between gap-4 pt-3">
        <Button type="button" variant="outline" size="sm" onClick={addRow}>
          <Plus className="size-3.5" />
          {t('orders.addBudgetPeriod')}
        </Button>
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
            <Switch checked={rollover} onCheckedChange={onRolloverChange} />
            {t('orders.budgetRollover')}
          </label>
          <div className="flex items-center gap-1.5 text-sm">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <Switch checked={smoothed} onCheckedChange={onSmoothedChange} />
              {t('orders.smoothed')}
            </label>
            <FieldTooltip text={t('orders.smoothedHelp')} />
          </div>
        </div>
      </CardFooter>
    </Card>
  )
}

// ── Related panel ───────────────────────────────────────────────────────────

const RELATED_TABS = ['lineitems', 'creatives'] as const
type RelatedTab = (typeof RELATED_TABS)[number]

function OrderRelatedPanel({ orderId, advertiserId, teamId }: { orderId: string; advertiserId?: string; teamId?: string }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<RelatedTab>('lineitems')
  const [statusFilter, setStatusFilter] = usePersistedState<'all' | 'ACTIVE' | 'INACTIVE'>('filters.orderCreatives.status', 'all')
  const liFilters = useLineItemFilters({ objectState: 'all' }, 'orderLineItems')
  const [lineItemSelection, setLineItemSelection] = useState<LineItem[]>([])
  const [creativeSelection, setCreativeSelection] = useState<Creative[]>([])
  const [bulkLoading, setBulkLoading] = useState(false)
  const [search, setSearch] = useState('')
  /** Whether the list's figures cover the order's period or its whole history. */
  const [statsPeriod, setStatsPeriod] = usePersistedState<'period' | 'all'>(
    'orderLineItems.statsPeriod',
    'period'
  )

  const { data: lineItems = [], isLoading: lineItemsLoading } = useQuery({
    queryKey: ['order-lineitems', orderId],
    queryFn: () => LineItemsApi.getByOrder(orderId),
  })
  const { data: creatives = [], isLoading: creativesLoading } = useQuery({
    queryKey: ['order-creatives', orderId],
    queryFn: () => CreativesApi.getByOrder(orderId),
  })

  const tabConfig: ListTab<RelatedTab>[] = [
    { key: 'lineitems', icon: <LayoutList className="size-3.5" />, label: t('orders.lineItemsTab'), count: lineItems.length },
    { key: 'creatives', icon: <Image className="size-3.5" />, label: t('orders.creativesTab'), count: creatives.length },
  ]

  const filteredLineItems = filterBySearch(liFilters.applyFilters(lineItems), search)
  const filteredCreatives = filterBySearch(statusFilter === 'all' ? creatives : creatives.filter((c) => c.objectState === statusFilter), search)

  /**
   * Both tabs activate and deactivate the same way — only the api the rows go
   * back to and the list to refetch differ.
   */
  const bulkSetStatus = async (
    state: 'ACTIVE' | 'INACTIVE',
    rows: { id: string; name: string }[],
    update: (id: string, input: { name: string; objectState: 'ACTIVE' | 'INACTIVE' }) => Promise<unknown>,
    queryKey: string,
    clearSelection: () => void,
  ) => {
    setBulkLoading(true)
    try {
      await Promise.all(rows.map((row) => update(row.id, { name: row.name, objectState: state })))
      queryClient.invalidateQueries({ queryKey: [queryKey, orderId] })
      toast.success(t('common.done'), { duration: 3000 })
      clearSelection()
    } catch (e) {
      toast.error(t('common.error'), { description: apiErrorDescription(e), duration: 5000 })
    } finally {
      setBulkLoading(false)
    }
  }

  const lineItemFlight = useMemo(() => spanFlight(lineItems), [lineItems])
  const [statsRange] = useStatsRange('24h', lineItemFlight)
  const resolvedRange = useResolvedRange(statsRange, lineItemFlight)
  const { statsByLineItem, totals: lineItemStatsTotals } = useLineItemStats(
    orderId,
    statsPeriod === 'all' ? null : resolvedRange,
    activeTab === 'lineitems'
  )

  const bulkSetLineItemStatus = (state: 'ACTIVE' | 'INACTIVE') =>
    bulkSetStatus(state, lineItemSelection, LineItemsApi.update, 'order-lineitems', () => setLineItemSelection([]))
  const bulkSetCreativeStatus = (state: 'ACTIVE' | 'INACTIVE') =>
    bulkSetStatus(state, creativeSelection, CreativesApi.update, 'order-creatives', () => setCreativeSelection([]))

  return (
    <ListCard className="pb-0">
      <CardHeader className="flex flex-row flex-wrap items-center gap-3">
        <BulkActionsSlot />
        <ListTabs tabs={tabConfig} value={activeTab} onValueChange={setActiveTab} />
        {activeTab === 'lineitems' ? (
          <LineItemFilters
            filters={liFilters.filters}
            onChange={liFilters.setFilters}
            defaults={liFilters.defaults}
            showAdvertiser={false}
          />
        ) : (
          <StatusFilterButton value={statusFilter} onChange={setStatusFilter} defaultValue="all" />
        )}
        <SearchInput value={search} onChange={setSearch} />
        {activeTab === 'lineitems' && (
          <Tabs
            value={statsPeriod}
            onValueChange={(value) => setStatsPeriod(value as 'period' | 'all')}
          >
            <TabsList>
              <TabsTrigger value="period">{t('orders.statsPeriod')}</TabsTrigger>
              <TabsTrigger value="all">{t('orders.statsAllTime')}</TabsTrigger>
            </TabsList>
          </Tabs>
        )}
        <ColumnsSlot />
        <div className="ml-auto flex items-center gap-2">
          {activeTab === 'lineitems' && (
            <CreateObjectButton type="lineItem" context={{ order: orderId, advertiser: advertiserId, team: teamId }} />
          )}
          <TableActionsSlot />
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {activeTab === 'lineitems' && (
          <>
            {lineItemSelection.length > 0 && (
              <BulkActionBar
                count={lineItemSelection.length}
                loading={bulkLoading}
                onActivate={() => bulkSetLineItemStatus('ACTIVE')}
                onDeactivate={() => bulkSetLineItemStatus('INACTIVE')}
                onClear={() => setLineItemSelection([])}
              />
            )}
            <LineItemTable
              lineItems={filteredLineItems}
              loading={lineItemsLoading}
              showAdvertiser={false}
              emptyMessage={t('orders.noLineItems')}
              selection={lineItemSelection}
              onSelectionChange={setLineItemSelection}
              stats={statsByLineItem}
              statsTotals={lineItemStatsTotals}
              columnsKey="orderLineItems"
            />
          </>
        )}
        {activeTab === 'creatives' && (
          <>
            {creativeSelection.length > 0 && (
              <BulkActionBar
                count={creativeSelection.length}
                loading={bulkLoading}
                onActivate={() => bulkSetCreativeStatus('ACTIVE')}
                onDeactivate={() => bulkSetCreativeStatus('INACTIVE')}
                onClear={() => setCreativeSelection([])}
              />
            )}
            <CreativeTable
              creatives={filteredCreatives}
              loading={creativesLoading}
              showAdvertiser={false}
              emptyMessage={t('orders.noCreatives')}
              selection={creativeSelection}
              onSelectionChange={setCreativeSelection}
            />
          </>
        )}
      </CardContent>
    </ListCard>
  )
}

// ── Inner form component ────────────────────────────────────────────────────

interface OrderEditFormProps {
  order: Order | null
  id: string
  isNew: boolean
  initialAdvertiserId?: string
  initialTeamId?: string
}

function OrderEditForm({ order, id, isNew, initialAdvertiserId = '', initialTeamId = '' }: OrderEditFormProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const createDialog = useCreateDialog()
  // A new object's cancel closes the create dialog, or leaves its page.
  const cancelNew = (path: string) => (createDialog ? createDialog.close() : navigate(path))
  const queryClient = useQueryClient()
  const { currentNetwork } = useNetwork()
  const currentUser = useCurrentUser()
  const { isEditing, startEditing, stopEditing } = useEditMode(isNew)
  const [copyOpen, setCopyOpen] = useState(false)
  // The copy endpoint wants one of the advertising write permissions, so a
  // read-only advertiser is not offered a button the server would refuse.
  const canCopy =
    !!currentNetwork &&
    !!currentUser?.permissionsByNetwork[currentNetwork.tagId]?.advertisingEditable

  // Derive default currency for new budget periods using the same fallback chain
  // as the legacy defaultCurrencyDeriver (sticky: first non-null wins):
  //   1. Advertiser's defaultCurrency
  //   2. Team's defaultCurrency
  //   3. User's preferredCurrency
  //   4. Network defaultCurrency
  const derivedCurrencyRef = useRef<string | null>(null)

  function deriveDefaultCurrency(advertiserId: string, teamId: string): string {
    if (!derivedCurrencyRef.current) {
      const advertiserCurrency = advertisers.find((a) => a.id === advertiserId)?.defaultCurrency
      const teamCurrency = teams.find((t) => t.id === teamId)?.defaultCurrency
      derivedCurrencyRef.current =
        advertiserCurrency ||
        teamCurrency ||
        currentUser?.preferredCurrency ||
        currentNetwork?.defaultCurrency ||
        'NOK'
    }
    return derivedCurrencyRef.current
  }

  // The order has no dates of its own, so the period its line items run over
  // stands in for its flight in the stats picker. Same query key as the related
  // panel's list, so the two share one fetch.
  const { data: orderLineItems = [] } = useQuery({
    queryKey: ['order-lineitems', id],
    queryFn: () => LineItemsApi.getByOrder(id!),
    enabled: !!id && !isNew,
  })
  const lineItemFlight = useMemo(() => spanFlight(orderLineItems), [orderLineItems])
  // Read from the saved order: spend is only worked out for budgets the server has.
  const budgetProgress = useMemo(() => activeBudgetProgress(order?.budget ?? []), [order?.budget])

  const { data: teams = [] } = useQuery({
    queryKey: ['teams'],
    queryFn: () => TeamsApi.getAll(),
  })
  const { data: advertisers = [] } = useQuery({
    queryKey: ['advertisers'],
    queryFn: () => AdvertisersApi.getAll(),
  })
  // An order for a political advertiser is marked as political.
  const { data: orderAdvertiser } = useQuery({
    queryKey: ['advertiser', order?.advertiserId],
    queryFn: () => AdvertisersApi.getById(order!.advertiserId),
    enabled: !!order?.advertiserId,
  })
  const isPolitical = !!orderAdvertiser?.advertiserType

  const [usersEnabled, setUsersEnabled] = useState(false)
  const { data: usersData, isFetching: usersFetching } = useQuery({
    queryKey: ['order-users'],
    queryFn: () => UsersApi.getAll({
      includeHidden: false,
      includeActive: true,
      includeInactive: false,
      minimal: true,
      orderBy: 'name',
      pageSize: 1001,
    }),
    // Also fetched unprompted when the order names people, so the read-only
    // card can show their display names instead of raw ids.
    enabled: usersEnabled || !!order?.salespersonUserId || !!order?.adOpsUserId,
    staleTime: Infinity,
  })
  const users = usersData?.results ?? []

  // The optimisation goal works on any network: the old UI only hid it outside
  // marketplaces. Custom events are the ones the network allows dynamic bidding on.
  const isMarketplace = !!(currentNetwork?.marketplacePlatform || currentNetwork?.hybridMarketplacePlatform)
  const { biddingCustomEvents: allowedCustomEvents, orderObjectives, cappingAlias } = useLineItemFieldSettings(isMarketplace)
  const optimiseCustomEvents = useBiddingCustomEvents(
    allowedCustomEvents,
    order?.optimiseFor?.customEventType?.id
  )
  const optimiseGoalLabel = (goal: BiddingGoalValue | '') => {
    if (!goal) return t('orders.optimiseForNone')
    const customEventId = customEventIdOfGoal(goal)
    return customEventId
      ? (optimiseCustomEvents.find((e) => e.id === customEventId)?.name ?? customEventId)
      : t(`lineItems.biddingGoals.${goal}`)
  }

  // Budget state — managed separately from TanStack form
  const initialBudgetJson = useRef(JSON.stringify(order?.budget ?? []))
  const initialRollover = useRef(order?.budgetRollover ?? false)
  const [budgetPeriods, setBudgetPeriods] = useState<BudgetPeriodRow[]>(
    () => (order?.budget ?? []).map((b, i) => toBudgetRow(b, i))
  )
  const [budgetRollover, setBudgetRollover] = useState(order?.budgetRollover ?? false)
  const initialSmoothed = useRef(order?.smoothed ?? false)
  const [smoothed, setSmoothed] = useState(order?.smoothed ?? false)

  // Caps are always editable too, so they live outside the form alongside the budget.
  const initialRateLimitsJson = useRef(JSON.stringify(order?.rateLimits ?? []))
  const [rateLimits, setRateLimits] = useState<RateLimit[]>(order?.rateLimits ?? [])
  const rateLimitsIsDirty = JSON.stringify(rateLimits) !== initialRateLimitsJson.current

  const budgetIsDirty =
    JSON.stringify(budgetPeriods.map((r) => ({ startDate: r.startDate, endDate: r.endDate, amount: r.amount, currency: r.currency }))) !== initialBudgetJson.current ||
    budgetRollover !== initialRollover.current ||
    smoothed !== initialSmoothed.current

  const form = useForm({
    defaultValues: {
      name: order?.name ?? '',
      externalReference: order?.externalReference ?? '',
      teamId: order?.teamId ?? initialTeamId,
      advertiserId: order?.advertiserId ?? initialAdvertiserId,
      active: order ? order.objectState === 'ACTIVE' : true,
      salespersonId: order?.salespersonUserId ?? '',
      adOpsId: order?.adOpsUserId ?? '',
      optimiseFor: (biddingGoalOf({ optimiseFor: order?.optimiseFor }) ?? '') as BiddingGoalValue | '',
    },
    onSubmit: ({ value }) => {
      if (hasRateLimitProblem(rateLimits)) {
        toast.error(t('lineItems.capping.errors.invalid'), { duration: 5000 })
        return
      }
      mutation.mutate({
        name: value.name,
        externalReference: value.externalReference,
        objectState: (order?.objectState ?? 'ACTIVE') as ObjectState,
        team: value.teamId ? { id: value.teamId } : undefined,
        advertiser: value.advertiserId ? { id: value.advertiserId } : undefined,
        budget: budgetPeriods.map((r) => ({
          startDay: r.startDate,
          endDay: r.endDate,
          amount: r.amount ? { currency: r.currency, amount: parseFloat(r.amount) } : undefined,
        })),
        budgetRollover,
        smoothed,
        rateLimits,
        salespersonUser: value.salespersonId ? { id: value.salespersonId } : null,
        adOpsUser: value.adOpsId ? { id: value.adOpsId } : null,
        optimiseFor: value.optimiseFor ? biddingEventTargetOf(value.optimiseFor) : { eventType: null },
      })
    },
  })
  const saveObjectState = useSaveObjectState(
    (objectState) => OrdersApi.update(id, { name: order?.name ?? '', objectState }),
    ['order', id],
    ['orders']
  )
  const { isDirty: formIsDirty, markClean, blocker } = useUnsavedChanges(form)
  const isDirty = formIsDirty || budgetIsDirty || rateLimitsIsDirty

  const mutation = useMutation({
    mutationFn: (input: OrderInput) => OrdersApi.update(id, input),
    onSuccess: (data) => {
      if (posthogConfigured) posthog.capture(isNew ? 'order_created' : 'order_updated', {
        has_advertiser: Boolean(data.advertiserId),
        has_team: Boolean(data.teamId),
        budget_period_count: data.budget.length,
      })
      queryClient.setQueryData(['order', id], data)
      queryClient.setQueryData(['orders'], (old: Order[] | undefined) => {
        if (!old) return [data]
        const idx = old.findIndex((o) => o.id === data.id)
        return idx >= 0 ? old.map((o) => (o.id === data.id ? data : o)) : [...old, data]
      })
      // Sync budget state to saved data
      initialBudgetJson.current = JSON.stringify(data.budget)
      initialRollover.current = data.budgetRollover
      setBudgetPeriods(data.budget.map((b, i) => toBudgetRow(b, i)))
      setBudgetRollover(data.budgetRollover)
      initialSmoothed.current = data.smoothed
      setSmoothed(data.smoothed)
      initialRateLimitsJson.current = JSON.stringify(data.rateLimits)
      setRateLimits(data.rateLimits)
      markClean()
      if (isNew) navigate(`/advertising/orders/${id}`, { replace: !createDialog })
      else stopEditing()
      toast.success(t('common.saved'), { description: isNew ? t('orders.createdSuccess') : t('orders.savedSuccess'), duration: 3000 })
    },
    onError: (error) => {
      toast.error(isNew ? t('orders.createFailed') : t('orders.saveFailed'), { description: apiErrorDescription(error), duration: 5000 })
    },
  })

  const crumbs = useObjectCrumbs(
    { advertiser: { id: order?.advertiserId, name: order?.advertiserName } },
    isNew ? t('orders.newOrder') : (order?.name ?? t('orders.editTitle'))
  )

  return (
    <AppShell crumbs={crumbs} showBack objectType={isNew ? undefined : t('objectTypes.order')}>
      <form
        className="ad-page"
        onSubmit={(e) => {
          e.preventDefault()
          form.handleSubmit()
        }}
      >
        <ValidationWarnings warnings={order?.validationWarnings} />
        {!isNew && <PoliticalLinkStatus advertiser={orderAdvertiser} />}
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <ObjectTypeLabel type={t('objectTypes.order')} />
            <h1>
              <form.Field
                name="name"
                validators={{
                  onChange: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                  onSubmit: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                }}
              >
                {(field) => (
                  <InlineEditTitle
                    value={field.state.value}
                    onChange={(v) => field.handleChange(v)}
                    placeholder={t('orders.namePlaceholder')}
                    required
                    error={field.state.meta.errors[0] as string | undefined}
                  />
                )}
              </form.Field>
            </h1>
          </div>
          {!isNew && (
            <div className="ad-page__actions">
              <VersionHistoryButton />
              {canCopy && (
                <Button
                  type="button"
                  variant="outline"
                  size="icon-sm"
                  title={t('orders.copyTitle')}
                  aria-label={t('orders.copyTitle')}
                  onClick={() => setCopyOpen(true)}
                >
                  <Copy className="size-4" />
                </Button>
              )}
                <ObjectStateToggle value={order!.objectState} onChange={saveObjectState} />
            </div>
          )}
        </div>

        {order && !isNew && canCopy && (
          <OrderCopyDialog order={order} open={copyOpen} onOpenChange={setCopyOpen} />
        )}

        {order && !isNew && (
          <StatsBar
            items={[
              {
                label: t('common.id'),
                content: <StatsBarId value={order.id} />,
              },
              {
                label: t('stats.activity'),
                content: (
                  <StatsBarActivity
                    created={formatDate(order.createTime)}
                  />
                ),
              },
              ...(order.executionState ? [{
                label: t('common.delivery'),
                content: <DeliveryStatusBadge status={order.executionState} />,
              }] : []),
            ]}
          />
        )}

        <div className="flex flex-col gap-3.5 lg:flex-row lg:items-stretch">
          {/* Left: form cards */}
          <div className="flex flex-col gap-3.5 flex-1 min-w-0">
            {!isEditing && order ? (
              <Card surface="highlight" className="flex-1">
                <ListCardHeader
                  title={
                    <>
                      <CardIcon icon={isPolitical ? Landmark : IdCard} />
                      {t('orders.details')}
                      {isPolitical && <PoliticalBadge />}
                    </>
                  }
                  actions={
                    <Button type="button" variant="tinted" size="sm" onClick={startEditing}>
                      <Pencil className="size-3.5" />
                      {t('common.edit')}
                    </Button>
                  }
                />
                <CardContent>
                  <DetailList>
                    <DetailRow label={t('common.advertiser')} icon={Building2}>
                      {order.advertiserId && (
                        <a
                          href={`/advertising/advertisers/${order.advertiserId}`}
                          onClick={(e) => {
                            e.preventDefault()
                            navigate(`/advertising/advertisers/${order.advertiserId}`)
                          }}
                          className="text-(--ad-link) hover:underline"
                        >
                          {advertisers.find((a) => a.id === order.advertiserId)?.name ?? order.advertiserName}
                        </a>
                      )}
                    </DetailRow>
                    <DetailRow label={t('common.team')} icon={Users}>
                      {order.teamId && (teams.find((tm) => tm.id === order.teamId)?.name ?? order.teamName)}
                    </DetailRow>
                    <DetailRow label={t('orders.externalReference')} icon={Hash}>
                      {order.externalReference}
                    </DetailRow>
                    <DetailRow label={t('orders.salespersonUser')} icon={User}>
                      {order.salespersonUserId && (users.find((u) => u.id === order.salespersonUserId)?.displayName ?? order.salespersonUserId)}
                    </DetailRow>
                    <DetailRow label={t('orders.adOpsUser')} icon={Headset}>
                      {order.adOpsUserId && (users.find((u) => u.id === order.adOpsUserId)?.displayName ?? order.adOpsUserId)}
                    </DetailRow>
                    <DetailRow label={t('orders.optimiseFor')} icon={Target}>
                      {optimiseGoalLabel(biddingGoalOf({ optimiseFor: order.optimiseFor }) ?? '')}
                    </DetailRow>
                  </DetailList>
                </CardContent>
              </Card>
            ) : (
            <Card>
              <CardHeader>
                <CardTitle>
                  <CardIcon icon={IdCard} />
                  {t('orders.details')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="field-grid">
                  <div className="p-field full">
                    <label>{t('common.team')}</label>
                    <form.Field name="teamId">
                      {(field) => (
                        <SearchSelect
                          value={field.state.value}
                          onChange={field.handleChange}
                          options={teams.map((team) => ({ value: team.id, label: team.name }))}
                          fallbackLabel={order?.teamName}
                          placeholder={t('advertisers.selectTeam')}
                          icon={Users}
                        />
                      )}
                    </form.Field>
                  </div>
                  <div className="p-field full">
                    <label>{t('nav.advertisers')}</label>
                    <form.Field name="advertiserId">
                      {(field) => (
                        <SearchSelect
                          value={field.state.value}
                          onChange={field.handleChange}
                          options={advertisers.map((adv) => ({ value: adv.id, label: adv.name }))}
                          fallbackLabel={order?.advertiserName}
                          placeholder={t('advertisers.selectAdvertiser')}
                          searchPlaceholder={t('advertisers.searchAdvertisers')}
                          emptyText={t('advertisers.noAdvertisersFound')}
                          icon={Building2}
                        />
                      )}
                    </form.Field>
                  </div>
                  <div className="p-field full">
                    <label>{t('orders.externalReference')}</label>
                    <form.Field name="externalReference">
                      {(field) => (
                        <Input
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          placeholder={t('orders.externalReferencePlaceholder')}
                        />
                      )}
                    </form.Field>
                  </div>
                  <div className="p-field">
                    <label>{t('orders.salespersonUser')}</label>
                    <form.Field name="salespersonId">
                      {(field) => (
                        <UserSelect
                          value={field.state.value}
                          onChange={field.handleChange}
                          users={users}
                          loading={usersFetching}
                          onOpen={() => setUsersEnabled(true)}
                        />
                      )}
                    </form.Field>
                  </div>
                  <div className="p-field">
                    <label>{t('orders.adOpsUser')}</label>
                    <form.Field name="adOpsId">
                      {(field) => (
                        <UserSelect
                          value={field.state.value}
                          onChange={field.handleChange}
                          users={users}
                          loading={usersFetching}
                          onOpen={() => setUsersEnabled(true)}
                        />
                      )}
                    </form.Field>
                  </div>
                  <div className="p-field full">
                    <FieldLabel tooltip={t('orders.optimiseForHelp')}>{t('orders.optimiseFor')}</FieldLabel>
                    <form.Field name="optimiseFor">
                      {(field) => (
                        <Select
                          label={t('orders.optimiseFor')}
                          value={field.state.value || 'NONE'}
                          onValueChange={(v) => field.handleChange(v === 'NONE' ? '' : (v as BiddingGoalValue))}
                        >
                          <SelectTrigger>
                            <SelectValue>{optimiseGoalLabel(field.state.value)}</SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="NONE">{t('orders.optimiseForNone')}</SelectItem>
                            {BIDDING_GOALS.map((g) => (
                              <SelectItem key={g} value={g}>
                                {t(`lineItems.biddingGoals.${g}`)}
                              </SelectItem>
                            ))}
                            {optimiseCustomEvents.map((e) => (
                              <SelectItem key={e.id} value={customBiddingGoal(e.id)}>
                                {e.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </form.Field>
                  </div>
                </div>
              </CardContent>
            </Card>
            )}

            {orderObjectives && (
              <BudgetCard
                periods={budgetPeriods}
                rollover={budgetRollover}
                currency={deriveDefaultCurrency(form.state.values.advertiserId, form.state.values.teamId)}
                onPeriodsChange={setBudgetPeriods}
                onRolloverChange={setBudgetRollover}
                smoothed={smoothed}
                onSmoothedChange={setSmoothed}
              />
            )}

            <CappingCard
              value={rateLimits}
              onChange={setRateLimits}
              types={ORDER_CAPPING_TYPES}
              alias={cappingAlias}
              // Orders are only ever capped per user.
              perUserOnly
            />
          </div>

          {/* Right: stats panel */}
          {!isNew && order && (
            /* Stacked on mobile the column dissolves (`contents`) so the chart
               leads the form and the progress card follows it. */
            <div className="contents lg:flex lg:min-w-0 lg:flex-1 lg:flex-col lg:gap-4">
              <div className="order-first lg:order-none">
                <StatsPanel
                  query={{ orderId: order.id, groupBy: 'HALF_HOURLY' }}
                  storageKey={`order-stats-${order.id}`}
                  flight={lineItemFlight}
                  variant="advertising"
                  auditObject={{ type: 'order', id: order.id }}
                />
              </div>
              {orderObjectives && budgetProgress && (
                <div className="order-last lg:order-none">
                  <DeliveryProgressCard
                    startDate={budgetProgress.startDate}
                    endDate={budgetProgress.endDate}
                    executionState={order.executionState}
                    deliveryPct={budgetProgress.deliveryPct}
                    elapsedTimePct={budgetProgress.elapsedTimePct}
                    objectives={{ BUDGET: budgetProgress.target }}
                    objectiveDelivery={
                      budgetProgress.spend ? { BUDGET: budgetProgress.spend } : undefined
                    }
                    surface="sunken"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Save bar */}
        <div className={`detail-savebar${isEditing || isDirty || mutation.isPending ? ' is-visible' : ''}`}>
          <div className="detail-savebar__left">
            {mutation.isPending && (
              <>
                <Loader2 className="size-4 animate-spin" />
                {isNew ? t('common.creating') : t('common.saving')}
              </>
            )}
          </div>
          <div className="detail-savebar__actions">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                if (isNew) {
                  cancelNew('/advertising/orders')
                  return
                }
                // Budget and caps live outside the form, so reset them by hand.
                setBudgetPeriods((order?.budget ?? []).map((b, i) => toBudgetRow(b, i)))
                setBudgetRollover(order?.budgetRollover ?? false)
                setSmoothed(order?.smoothed ?? false)
                setRateLimits(order?.rateLimits ?? [])
                form.reset()
                stopEditing()
              }}
              disabled={mutation.isPending}
            >
              <X className="size-4" />
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              loading={mutation.isPending}
            >
              {isNew ? <Plus className="size-4" /> : <Check className="size-4" />}
              {isNew ? t('orders.newOrder') : t('common.save')}
            </Button>
          </div>
        </div>

        {!isNew && <OrderRelatedPanel orderId={id} advertiserId={order?.advertiserId} teamId={order?.teamId} />}
      </form>
      <UnsavedChangesDialog blocker={blocker} />
    </AppShell>
  )
}

// ── Outer page ──────────────────────────────────────────────────────────────

export function OrderEditPage() {
  const { t } = useTranslation()
  const { id, searchParams } = useEditRoute()
  const isNew = searchParams.get('new') === 'true'
  const initialAdvertiserId = isNew ? (searchParams.get('advertiser') ?? '') : ''
  const initialTeamId = isNew ? (searchParams.get('team') ?? '') : ''
  const hasToken = AuthService.isAuthenticated()

  const { data: order, isLoading, isError } = useQuery({
    queryKey: ['order', id],
    queryFn: () => OrdersApi.getById(id!),
    enabled: !!id && !isNew && hasToken,
    retry: false,
  })

  const crumbs = useObjectCrumbs(
    { advertiser: { id: order?.advertiserId, name: order?.advertiserName } },
    isNew ? t('orders.newOrder') : (order?.name ?? t('orders.editTitle'))
  )

  if (isError && !isNew) {
    return (
      <AppShell crumbs={crumbs} showBack>
        <NetworkObjectLookup objectId={id!} />
      </AppShell>
    )
  }

  if (isLoading && !isNew) {
    return (
      <AppShell crumbs={crumbs} showBack>
        <FormSkeleton rows={3} columns={2} />
      </AppShell>
    )
  }

  return <OrderEditForm key={id} order={order ?? null} id={id!} isNew={isNew} initialAdvertiserId={initialAdvertiserId} initialTeamId={initialTeamId} />
}
